import { MAIN_ROUTES } from 'routes';
import VacationBanner from 'components/VacationBanner/VacationBanner';
import LeftNavigationBar from './components/LeftNavigationBar';
import TopNavigationBar from './components/TopNavigationBar';
import UnverifiedEmailBanner from 'components/UnverifiedEmailBanner/UnverifiedEmailBanner';
import UnverifiedPhoneBanner from 'components/UnverifiedPhoneBanner/UnverifiedPhoneBanner';
import { ImpersonationBanner } from 'components/ImpersonationBanner';
import MaintenanceBanner from 'components/MaintenanceBanner/MaintenanceBanner';
import LoginPromptModal from 'components/LoginPromptModal/LoginPromptModal';
import { SmsAlertsLaunchAnnouncementModal } from 'components/SmsAlertsLaunchAnnouncementModal';
import Footer from 'components/Footer';
import './Main.css';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useSmsAlertsLaunchAnnouncementGate } from 'hooks/useSmsAlertsLaunchAnnouncementGate';
import { useIsMobile } from 'hooks/useIsMobile';

interface Props {
  currentTab: (typeof MAIN_ROUTES)[number];
  backgroundImage?: string; // Optional blur background
  backgroundFlipX?: boolean; // Mirror the blur background horizontally
}

export const Main = ({ currentTab, backgroundImage, backgroundFlipX = false }: Props) => {
  const { data: userData } = useGetMeQuery();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const menuRef = useRef<HTMLDivElement>(null);

  // One-time post-login SMS Alerts launch announcement. The hook is safe to
  // call for unauthenticated visitors (the inner /me + have-list queries
  // skip cleanly), and the modal renders nothing until all gate conditions
  // are met.
  const smsLaunchAnnouncement = useSmsAlertsLaunchAnnouncementGate();

  // The mobile menu is an overlay that only exists below the breakpoint, so it
  // must not stay open when the viewport grows past it.
  useEffect(() => {
    if (!isMobile) setIsMobileMenuOpen(false);
  }, [isMobile]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMobile || !isMobileMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Ignore clicks on the hamburger button (let its onClick handler manage the toggle)
      if (target.closest('.mobile-hamburger')) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(target)) {
        setIsMobileMenuOpen(false);
      }
    };

    // Add listener with a slight delay to avoid closing immediately after opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="App">
      <div className="main">
        {/* Sidebar */}
        <LeftNavigationBar
          ref={menuRef}
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
          isPublicMode={!currentUser}
          onNavigationAttempt={() => setShowLoginModal(true)}
          isAuthenticated={!!currentUser}
        />

        {/* Main content area */}
        <div className="content">
          {/* Header stays above blur */}
          <TopNavigationBar
            title={currentTab.title ?? ''}
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />

          {/* Impersonation banner - shows when admin is impersonating a user */}
          {localStorage.getItem('isImpersonating') === 'true' && (
            <ImpersonationBanner
              impersonatedUsername={localStorage.getItem('impersonatedUsername') || ''}
              targetUserId={parseInt(localStorage.getItem('impersonatedUserId') || '0', 10)}
            />
          )}

          {/* Maintenance mode banner - shows when maintenance mode is active and user is admin */}
          <MaintenanceBanner />

          {/* Email verification banner - Priority 1 (hidden on home page to reduce clutter with onboarding checklist) */}
          {currentTab.path !== '/home' && <UnverifiedEmailBanner />}

          {/* Phone verification banner - Priority 2 (only shows if email is verified, hidden on home page) */}
          {currentTab.path !== '/home' && userData?.emailVerified && <UnverifiedPhoneBanner />}

          {/* Vacation Mode banner - every authenticated page, home included; renders nothing when off */}
          <VacationBanner />

          {/* Content with optional background blur */}
          <div className="main-content-wrapper">
            {backgroundImage && (
              <div
                className="main-blur-bg"
                style={{ backgroundImage: `url('${backgroundImage}')`, transform: backgroundFlipX ? 'scaleX(-1)' : undefined }}
              />
            )}

            <div className="main-page-content">{currentTab.Component}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Login prompt modal for unauthenticated users */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername=""
      />

      {/* One-time SMS Alerts launch announcement (post-login only). */}
      <SmsAlertsLaunchAnnouncementModal
        isOpen={smsLaunchAnnouncement.isOpen}
        onPrimary={smsLaunchAnnouncement.handlePrimary}
        onDismiss={smsLaunchAnnouncement.handleDismiss}
      />
    </div>
  );
};
